<template>
  <div>
    <v-container v-if="loading">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && auditMessages">

      <!-- Audit Messages -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h5">Audit Details</span>
            </v-card-title>
            <v-card-text class="ma-0 pa-0">
              <v-container class="ma-0 pa-0">

                <v-data-table
                  dense
                  :headers="auditHeaders"
                  :items="auditMessages"
                  :loading="loading"
                  :page.sync="page"
                  @page-count="pageCount = $event"
                  :footer-props="footerProps"
                  :hide-default-footer="false"
                  item-key="id"
                  fixed-header
                  id="auditMessagesTable">
                  <template v-slot:item="{ item }">
                    <tr :key="item.id">
                      <td>{{ item.time }}</td>
                      <td>{{ item.userName }}</td>
                      <td>{{ item.api }}</td>
                      <td>{{ item.method }}</td>
                      <td><compare-audit-dialog
                            :contentBefore="item.contentBefore"
                            :contentAfter="item.contentAfter"
                          /> </td>
                    </tr>
                  </template>
                </v-data-table>

                <!-- External Pagination -->
                <div class="text-center ma-0 pa-0">
                  <v-pagination
                    v-model="page"
                    :length="pageCount"
                  ></v-pagination>
                </div>

              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import {
  AuditMessage,
  AuditMessageResource,
} from '@/api/auditmessages';
import AuditMessageCompareDialog from '@/components/AuditMessageCompareDialog.vue';

const jsdiff = require('diff');

@Component({
  computed: mapState(['fiId']),
  components: {
    'compare-audit-dialog': AuditMessageCompareDialog,
  },
})
export default class AuditMessages extends Vue {
  @Inject('AuditMessageResource')
  auditMessageResource!: AuditMessageResource;

  fiId!: number;

  auditMessages: AuditMessage[] = [];

  loading: boolean = false;

  page: number = 1;

  pageCount: number = 0;

  auditHeaders:DataTableHeader[] = [
    { text: 'Time', value: 'time', sortable: true },
    { text: 'User Name', value: 'userName', sortable: true },
    { text: 'Page', value: 'api', sortable: true },
    { text: 'Method', value: 'name', sortable: false },
  ];

  footerProps = {
    'items-per-page-options': [20, 25, 50, 100, -1],
  }


  @Watch('fiId')
  mounted() {
    this.loading = true;

    this.auditMessageResource.getAll(this.fiId)
      .then((result:AuditMessage[]) => {
        this.auditMessages = result;
        for (let i = 0; i < this.auditMessages.length; ++i) {
          this.auditMessages[i].api = this.getFriendlyName(this.auditMessages[i].api);
        }
        this.loading = false;
      })
      .catch((error:any) => {
        this.showError(error);
        this.loading = false;
      });
  }

  getFriendlyName = (apiString: string) => {
    if (!apiString) {
      return '';
    }

    let friendlyNameToReturn = 'Unknown';
    AuditMessages.friendlyNameMap.forEach((friendlyName: string, pathFragment: string) => {
      if (apiString.includes(pathFragment)) {
        friendlyNameToReturn = friendlyName;
      }
    });
    return friendlyNameToReturn;
  }

  static friendlyNameMap: Map<string, string> =
    new Map([
      ['/transaction-classification-rule', 'Transaction Classification Rules'],
      ['/transaction-classifications', 'Transaction Classification Rules'],

      ['/relationship-classification-rule', 'Relationship Classification'],
      ['/relationship-classifications', 'Relationship Classification'],

      ['/core-statement-code-mapping-rule', 'Statement Codes'],
      ['/core-statement-code-mapping', 'Statement Codes'],

      ['/account-type-mapping-rule', 'Account Types'],
      ['/account-type-mapping', 'Account Types'],

      ['/closed-account-settings', 'Closed Account Settings'],
      ['/dormant-account-settings', 'Dormant Account Settings'],

      ['/interest', 'Interest Settings'],

      ['/atm', 'ATM Settings'],

      ['/engagement-rewards', 'Engagement Rewards'],

      ['/kasasa-account-upgrades', 'Kasasa Upgrades'],

      ['/value-added-fees-config', 'FI Value Added Fees'],
      ['/kupgrade-core-config', 'FI OLB Core Config'],
      ['/kupgrade-doc-config', 'FI OLB DocSign Config'],
      ['/kupgrade-doc-match-type', 'OLB DocSign Match-Types'],
      ['/kupgrade-feature-config', 'FI OLB Feature Config'],
      ['/kupgrade-olb-config', 'FI OLB Gateway Config'],
      ['/saml-providers', 'OLB SAML Providers'],
      ['/olb-eligibility-config', 'FI OLB Eligibility'],

      ['/cycle-set', 'Cycle Sets'],
      ['/cycle', 'Cycle Sets'],

      ['/audit', 'Audit Messages'],

      ['/processing-days', 'Processing Days & Holidays'],
      ['/most-recent-processing-day', 'Processing Days & Holidays'],
      ['/most-recent-future-processing-day', 'Processing Days & Holidays'],
      ['/holiday', 'Processing Days & Holidays'],

      ['/product-mapping', 'Product Mappings'],
      ['/account-product-types', 'Product Mappings'],
      ['/product-types', 'Product Mappings'],

      ['/product', 'Products'],

      ['/fi-configuration', 'FI Configuration'],

      ['/eoc-email-settings', 'End of Cycle Email'],
      ['/eoc-email-settings/events', 'End of Cycle Email'],
      ['/send-eoc-email', 'End of Cycle Email'],
      ['/test-eoc-email', 'End of Cycle Email'],
      ['/query-eoc-email', 'End of Cycle Email'],

      ['/config-version', 'Core Management'],

      ['/config-product-mapping', 'External'],
      ['/processing-product', 'External'],
      ['/config-data-export', 'External'],
      ['/config-cycles', 'External'],
      ['/qual-override', 'External'],
      ['/keymap-config', 'AKK/CKK Mapping Config']

    ]);

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss">
</style>
